import React, { useState } from 'react'
import { Button, Menu, MenuItem, ListSubheader } from '@mui/material'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'

interface SampleTextSelectorProps {
  onSelect: (text: string) => void
}

const SAMPLE_TEXTS: Array<{ group: string; items: Array<{ label: string; text: string }> }> = [
  {
    group: 'English',
    items: [
      {
        label: 'News paragraph',
        text:
          'Scientists have discovered a new species of deep-sea fish in the Pacific Ocean, living at depths of over three thousand meters. The creature, which emits bioluminescent light, has never been observed before and could shed light on evolutionary adaptations in extreme environments.',
      },
      {
        label: 'Technical description',
        text:
          'The transformer architecture relies on self-attention mechanisms that compute weighted sums of value vectors, allowing each token to attend to all other tokens in the sequence. This design enables parallel computation and long-range dependency capture, making it highly effective for natural language processing tasks.',
      },
      {
        label: 'Casual speech',
        text:
          "Hey, so I was thinking we could grab some coffee later and catch up. It's been ages since we last talked, and I heard you've got some really exciting news to share. Let me know what works for you!",
      },
    ],
  },
  {
    group: 'Français',
    items: [
      {
        label: 'Paragraphe d\'actualité',
        text:
          "Les chercheurs de l'Institut Pasteur ont annoncé une avancée significative dans le traitement des maladies auto-immunes grâce à une nouvelle molécule capable de moduler la réponse immunitaire sans effets secondaires majeurs. Les essais cliniques débuteront au premier trimestre.",
      },
      {
        label: 'Description technique',
        text:
          "Le système de reconnaissance vocale repose sur des réseaux de neurones profonds entraînés sur des millions d'heures de parole. Il utilise des modèles acoustiques et de langage combinés pour transcrire la parole en temps réel avec une précision supérieure à quatre-vingt-quinze pour cent.",
      },
      {
        label: 'Conversation',
        text:
          "Bonjour ! Alors, comment s'est passé ton week-end ? J'espère que tu as pu te reposer un peu. De mon côté, j'ai visité une exposition très intéressante au musée d'Art moderne. Il faudra qu'on y retourne ensemble la prochaine fois.",
      },
    ],
  },
  {
    group: 'Deutsch',
    items: [
      {
        label: 'Nachrichtenabsatz',
        text:
          'Die Bundesregierung hat heute ein umfangreiches Investitionspaket für erneuerbare Energien vorgestellt. Das Programm sieht Investitionen von über zwanzig Milliarden Euro in Solar- und Windenergie vor und soll bis zum Jahr zweitausenddreißig umgesetzt werden.',
      },
      {
        label: 'Technische Beschreibung',
        text:
          'Das neuronale Netzwerk verarbeitet Audiodaten in Echtzeit mithilfe eines mehrschichtigen Encoders, der spektrale Merkmale extrahiert und in hochdimensionale Vektoren umwandelt. Die Dekoderkomponente generiert dann die entsprechende Textsequenz mithilfe eines Aufmerksamkeitsmechanismus.',
      },
      {
        label: 'Alltagsgespräch',
        text:
          'Guten Morgen! Wie geht es dir? Ich habe gehört, dass du letztes Wochenende einen tollen Ausflug gemacht hast. Erzähl mal, wie war es? Ich würde so gerne auch mal wieder raus in die Natur.',
      },
    ],
  },
]

const SampleTextSelector: React.FC<SampleTextSelectorProps> = ({ onSelect }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (text: string) => {
    onSelect(text)
    handleClose()
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<TextSnippetIcon />}
        onClick={handleOpen}
        sx={{
          borderColor: 'rgba(0, 74, 87, 0.5)',
          color: '#004a57',
          textTransform: 'none',
          '&:hover': {
            borderColor: '#004a57',
            backgroundColor: 'rgba(0, 74, 87, 0.08)',
          },
        }}
      >
        Sample Text
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            backgroundColor: 'background.paper',
          },
        }}
      >
        {SAMPLE_TEXTS.map((group) => [
          <ListSubheader key={`header-${group.group}`} sx={{ fontWeight: 700, lineHeight: '32px' }}>
            {group.group}
          </ListSubheader>,
          ...group.items.map((item) => (
            <MenuItem
              key={`${group.group}-${item.label}`}
              onClick={() => handleSelect(item.text)}
              sx={{ whiteSpace: 'normal', maxWidth: 360, fontSize: '0.875rem' }}
            >
              {item.label}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  )
}

export default SampleTextSelector
